import type { Dispatch, SetStateAction } from 'react';
import { useCallback, useRef, useState } from 'react';

type UseControlledProps<V = unknown> = {
  state?: V;
  defaultState?: V;
  onChange?: (value: V) => void;
};

export const useControlled = <V = unknown>({
  state,
  defaultState,
  onChange,
}: UseControlledProps<V>): [V, Dispatch<SetStateAction<V>>] => {
  const { current: isControlled } = useRef(state !== undefined);
  const [internalValue, setInternalValue] = useState(defaultState);
  const value = (isControlled ? state : internalValue) as V;

  const setValue: Dispatch<SetStateAction<V>> = useCallback(
    (newValueAction) => {
      const newValue =
        typeof newValueAction === 'function' ? (newValueAction as (prevState: V) => V)(value) : newValueAction;

      if (value !== newValue) {
        onChange?.(newValue);
      }

      if (!isControlled) {
        setInternalValue(newValue);
      }
    },
    [isControlled, onChange, value]
  );

  return [value, setValue];
};
